import { Token } from './Token.js'
import { Storage } from './Storage.js'
import { getAppId } from './Digest.js'
import { ParamExtractor } from './ParamExtractor.js'

/**
 * @module
 * Provides convenience support for WebDaemon apps delivered
 * through browsers.
 *
 * Use the singleton pattern, providing app name on first call
 * to getInstance.
 */

const PARTY_PARAM = 'party'
const PREFIX_PARAM = 'prefix'
const LAST_KNOWN_DAEMON = 'lastKnownDaemon' // In app's localStorage.

export class BrowserApp {

  /** @type {BrowserApp | null} */
  static #instance

  #appName
  #partyKey
  #tokensKey
  #appUrlKey

  /**
   * Gets the singleton instance of BrowserApp. On invocations
   * after the first, the app name must be omitted or match
   * the first-used app name.
   *
   * @param {string=} appName
   * @return {Promise<BrowserApp>} instance of browser app.
   */
  static async getInstance(appName = undefined) {
    if (
      !appName &&
      !BrowserApp.#instance
    ) {
      throw `Must provide app name`
    }

    if (
      appName &&
      BrowserApp.#instance &&
      appName !== BrowserApp.#instance.#appName
    ) {
      throw `Cannot change app name from ${BrowserApp.#instance.#appName} to ${appName}`
    }

    if (appName && !BrowserApp.#instance) {
      BrowserApp.#instance = new BrowserApp(appName)
      await BrowserApp.#instance.init()
    }

    if (!BrowserApp.#instance) {
      throw `No instance of BrowserApp`
    }

    return BrowserApp.#instance
  }

  /**
   * Constructs a BrowserApp instance with the app name. This should be
   * unique per origin. Spaces are _NOT_ allowed in this name.
   *
   * @param {string} appName a unique name for this app per origin.
   */
  constructor(appName) {
    this.#appName = appName
    this.#partyKey = `${appName}Party`
    this.#tokensKey = `${appName}Tokens`
    this.#appUrlKey = `${appName}AppUrl`
  }

  /**
   * Initialises the instance with the party name and tokens passed
   * in the #fragment and ?query string search parameters.
   *
   *   - party is passed in the special parameter 'party'.
   *   - tokens are passed in the remaining parameters keyed by host name.
   *
   * The #fragment starting at the first 'æ' (if present) is used and
   * any params whose name starts with the special character 'æ' are extracted.
   *
   * Any fragment prior to this is retained in the browser location.
   *
   * Any query params whose name starts with the special character 'æ' are
   * extracted.
   *
   * The window location is replaced with the 'clean' version that
   * no longer includes the tokens.
   *
   * @return {Promise<BrowserApp>} instance promise.
   * @throws {string} error if party token `src` does not match our window location.
   */
  async init() {
    const ourUrl = new URL(globalThis.location.href)

    const extractor = new ParamExtractor()
    const newHash = extractor.extractHash(ourUrl.hash)
    const newSearch = extractor.extractQuery(ourUrl.search)
    const daemonParams = extractor.getParams()

    if (daemonParams.has(PARTY_PARAM)) {
      sessionStorage[this.#tokensKey] = daemonParams.toString()
      const identityToken = this.getToken()
      await identityToken.verifySignatory()
      sessionStorage[this.#partyKey] = identityToken.getParty()
      localStorage.setItem(LAST_KNOWN_DAEMON, identityToken.getParty())

      const srcUrl = identityToken.getSourceUrl()
      if (
        srcUrl.origin !== ourUrl.origin ||
        srcUrl.pathname !== ourUrl.pathname ||
        srcUrl.search !== ourUrl.search
      ) {
        throw `Party token is for different app: ${srcUrl}`
      }
    }

    ourUrl.hash = newHash
    ourUrl.search = newSearch
    if (
      daemonParams.has(PARTY_PARAM) ||
      sessionStorage[this.#appUrlKey] === undefined
    ) {
      sessionStorage[this.#appUrlKey] = ourUrl.toString()
    }
    globalThis.history.replaceState(null, '', ourUrl.toString())
    return this
  }

  /**
   * Returns true if this app is an orphan, i.e. has not been launched from
   * a Dæmon shell.
   *
   * Otherwise returns false.
   *
   * @return {boolean} true if an orphan without Dæmon, otherwise false.
    */
  isOrphan() {
    return (
      sessionStorage[this.#partyKey] === undefined ||
      sessionStorage[this.#tokensKey] === undefined
    )
  }

  /**
   * Returns the app name. This is normally used to disambiguate properties
   * of this app from others, such as those sharing a web origin and therefore
   * sharing localStorage or sessionStorage.
   *
   * Note that this is a client-side name not known to the tabserver.
   *
   * @return {string} the app name.
   */
  getAppName() {
    return this.#appName
  }

  /**
   * Returns the app url, obtained from window.location at time of app init,
   * or the empty string if not initialised.
   *
   * @return {string} the app url, or the empty string.
   */
  getAppUrl() {
    return sessionStorage[this.#appUrlKey]
  }

  /**
   * Returns the party hostname, being the Dæmon that launched this app.
   *
   * @return {string} the party (Dæmon) hostname.
   */
  getParty() {
    return sessionStorage[this.#partyKey]
  }

  /**
   * Returns the party origin, being the protocol (http: or https:)
   * and the party hostname.
   *
   * @return {string} the party origin.
   */
  getPartyOrigin() {
    return `${globalThis.location.protocol}//${this.getParty()}`
  }

  /**
   * Returns the default prefix associated with the app url if specified,
   * or this app if not. The default prefix is simply the app ID being a
   * short digest of the app URL, excluding any # fragment.
   *
   * @param {string | URL | null} appUrl for which the default prefix is required (optional).
   * @return {Promise<string>} the default prefix for the app.
   */
  getDefaultPrefix(appUrl = null) {
    if (!appUrl) {
      appUrl = this.getAppUrl()
    }

    const cleanUrl = new URL(appUrl)
    cleanUrl.hash = ''
    return getAppId(cleanUrl)
  }

  /**
   * Returns the likely prefix for this app.
   *
   * This is the value of the prefix parameter if present,
   * or the default prefix for the app if not.
   *
   * @return {Promise<string>} prefix obtained from prefix= param, or computed from the app URL.
   */
  getPrefix() {
    if (this.hasParam(PREFIX_PARAM)) {
      return Promise.resolve(this.getParam(PREFIX_PARAM) ?? '')
    }
    return this.getDefaultPrefix()
  }

  /**
   * Returns the likely url for the (optional) named tab on this app's agent.
   *
   * This will be wrong if the YAML file has a `prefix=` specified which
   * is not set to the value of a provided `prefix=` parameter.
   *
   * @param {string?} tab name, e.g. 'v1'.
   * @returns {Promise<string>} the url of this tab on this app.
   */
  async getAgentUrl(tab) {
    const origin = this.getPartyOrigin()
    const prefix = await this.getPrefix()
    const agentUrl = `${origin}/tab/${prefix}`
    return tab ? `${agentUrl}/${tab}` : agentUrl
  }

  /**
   * @typedef {object} AgentEndpointOptions
   * @property {string=} party party hostname. Defaults to the launching party.
   * @property {string | URL=} appUrl app URL used to derive the protocol and default prefix.
   * @property {string=} prefix agent prefix. Defaults to the app's default prefix.
   * @property {string=} tab optional tab name appended to the endpoint.
   */

  /**
   * Returns the agent endpoint associated with the party, app, prefix and tab
   * where all options are, well, optional.
   *
   * @param {AgentEndpointOptions} options
   * @returns {Promise<string>} the agent endpoint.
   */
  async getAgentEndpoint(options) {
    const {
      party = this.getParty(),
      appUrl = this.getAppUrl(),
      prefix = await this.getDefaultPrefix(appUrl),
      tab
    } = options

    const protocol = new URL(appUrl).protocol
    const appEndpoint = `${protocol}//${party}/tab/${prefix}`
    return tab ? `${appEndpoint}/${tab}` : appEndpoint
  }

  /**
   * Returns the base64-encoded token for the supplied party. If no
   * party is specified, returns the token for the launching party.
   *
   * @param {string} party the party, or the launching party if not specified.
   * @return {string} the base64-encoded token for the party.
   */
  getTokenBase64(party = PARTY_PARAM) {
    const tokens = sessionStorage[this.#tokensKey]
    const searchParams = new URLSearchParams(tokens)
    const tokenBase64 = searchParams.get(party)
    if (!tokenBase64) {
      throw `No token for ${party}, check 'audience' in YML`
    }
    return tokenBase64
  }


  /**
   * Gets the token for a party either as a base64-encoded string, or as a
   * Token object depending on the asBase64 argument.
   *
   * If the party is not passed or is null, the default is the launching party.
   * If the asBase64 is not passed, the default is true.
   *
   * @param {string=} party the audience for the token. Defaults to the identity token 'party'.
   * @return {Token} the pre-generated token for the given party.
   */
  getToken(party = PARTY_PARAM) {
    const tokenBase64 = this.getTokenBase64(party)
    return new Token(tokenBase64)
  }

  /**
   * Gets the value of the named parameter, or null if no value is supplied.
   *
   * The parameter name is case insensitive.
   *
   * The parameter is specified as:
   *   - an attribute on the webdaemon meta tag, or
   *   - a search parameter in the query string of the URL
   *
   * where the query string parameter takes priority if present.
   *
   * @param {string} name
   * @return {string | null} parameter value or null if not present.
   */
  getParam(name) {

    // Return a matching search parameter if present.
    const searchParams = new URL(globalThis.location.href).searchParams
    for (const [paramName, value] of searchParams) {
      if (name.toLowerCase() == paramName.toLowerCase()) {
        return value
      }
    }

    // Return meta element attribute if present, or null.
    const metaElement = document.querySelector('link[rel=webdaemon]')
    return metaElement?.getAttribute(name) ?? null
  }

  /**
   * Returns true if the named parameter is present, even if with empty
   * or null value.
   *
   * @param {string} name
   * @return {boolean} true if the parameter exists, otherwise false.
   */
  hasParam(name) {
    const searchParams = new URL(globalThis.location.href).searchParams
    if (searchParams.has(name)) {
      return true
    }

    const metaElement = document.querySelector('link[rel=webdaemon]')
    return metaElement?.hasAttribute(name) ?? false
  }

  /**
   * Returns the currently raised launch alert for this app,
   * or null if none.
   *
   * @typedef {import('./Alert.js').AlertEntry} AlertEntry
   *
   * @return { Promise<AlertEntry | null> } raised launch alert, or null if none.
   * @throws {string} exception if alert catalog cannot be retrieved.
   */
  async getLaunchAlert() {
    const origin = this.getPartyOrigin()
    const url = new URL(`${origin}/catalog`)
    url.searchParams.append('alert', '')
    const response = await fetch(url, {
      headers: {
        'X-Tabserver-Token': this.getTokenBase64(),
        'Accept': 'application/json'
      }
    })
    const json = await response.json()
    if ('error' in json) {
      throw json.error
    }

    const alert = json.ok.alert.find(
      /** @param {AlertEntry} alert */
      alert => (
        alert.type == 'LAUNCH' &&
        alert.sourceUrl == new URL(this.getAppUrl())
      )
    )
    return alert || null
  }

  /**
   * Resolves the currently raised launch alert for this app, if any.
   *
   * @throws {string} exception if alert cannot be resolved.
   */
  async resolveLaunchAlert() {
    const origin = this.getPartyOrigin()
    const url = new URL(`${origin}/resolve`)
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'X-Tabserver-Token': this.getTokenBase64(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: 'LAUNCH',
        source: this.getAppUrl()
      })
    })
    const json = await response.json()
    if ('error' in json) {
      throw json.error
    }
  }

  /**
   * Resolves the currently raised launch alert for this app, if any.
   *
   * @throws {string} exception if alert cannot be resolved.
   */
  async rejectLaunchAlert() {
    const origin = this.getPartyOrigin()
    const url = new URL(`${origin}/reject`)
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'X-Tabserver-Token': this.getTokenBase64(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: 'LAUNCH',
        source: this.getAppUrl()
      })
    })
    const json = await response.json()
    if ('error' in json) {
      throw json.error
    }
  }

  /**
   * Gets a Storage instance using the party token.
   *
   * @returns {Storage} instance.
   */
  getStorage() {
    return new Storage(this.getToken())
  }
}
